import type { ReactNode } from "react";
import { APP_NAME } from "@grundbesitz/shared";
import type { DeckId, PublicGameState } from "@grundbesitz/shared";
import { formatMoney } from "../../lib/format";
import { HotelIcon, HouseIcon } from "./BuildingIcons";
import { DiceView } from "./DiceView";
import { FieldGlyph } from "./FieldGlyph";
import type { DiceDisplay } from "./useBoardMotion";
import "./board.css";

const DECKS: Record<DeckId, { title: string; aria: string }> = {
  chance: { title: "Ereignis", aria: "Ereigniskarten" },
  community: { title: "Gemeinschaft", aria: "Gemeinschaftskarten" },
};

function Deck({ id, count }: { id: DeckId; count: number }) {
  const { title, aria } = DECKS[id];
  return (
    <div className={`gb-deck gb-deck-${id}`} role="img" aria-label={`${aria}, ${count} im Stapel`}>
      <span className="gb-deck-card gb-deck-back-2" />
      <span className="gb-deck-card gb-deck-back-1" />
      <div className="gb-deck-card gb-deck-top">
        <FieldGlyph kind={id} />
        <b>{title}</b>
        <i>{count} Karten</i>
      </div>
    </div>
  );
}

/**
 * The middle of the board as HTML above the SVG. All sizes use container units of the board (cqw),
 * so it scales with the plan. `center` fills the free area in between and scrolls when it overflows.
 */
export function BoardCenter({ game, dice, center }: { game: PublicGameState; dice: DiceDisplay; center?: ReactNode }) {
  const showPot = game.options.houseRules.freeParkingPot;
  return (
    <div className="gb-center">
      <Deck id="chance" count={game.deckSizes.chance} />
      <div className="gb-wordmark">{APP_NAME}</div>
      {showPot ? (
        <div className="gb-pot" role="img" aria-label={`Frei-Parken-Topf: ${formatMoney(game.freeParkingPot)}`}>
          <span>Frei Parken</span>
          <b>{formatMoney(game.freeParkingPot)}</b>
        </div>
      ) : (
        <div />
      )}

      {center ? (
        <div className="gb-center-slot">{center}</div>
      ) : (
        <div className="gb-center-dice">
          <DiceView dice={dice.dice} rolling={dice.rolling} rollId={dice.rollId} size="10cqw" />
        </div>
      )}

      {center ? <DiceView dice={dice.dice} rolling={dice.rolling} rollId={dice.rollId} size="6.6cqw" /> : <div />}
      <div className="gb-bank" role="img" aria-label={`Bank: ${game.bank.houses} Häuser, ${game.bank.hotels} Hotels`}>
        <span>
          <HouseIcon />
          {game.bank.houses}
        </span>
        <span>
          <HotelIcon />
          {game.bank.hotels}
        </span>
      </div>
      <Deck id="community" count={game.deckSizes.community} />
    </div>
  );
}
